import { createReducer } from '@reduxjs/toolkit';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import {
  currentExpertUpdated,
  fetchInitialExpertsPending,
  fetchInitialExpertsSucceed,
  fetchInitialExpertsFailed,
  fetchAllExpertsPending,
  fetchAllExpertsSucceed,
  fetchAllExpertsFailed,
  fetchExpertsByCategoriesPending,
  fetchExpertsByCategoriesSucceed,
  fetchExpertsByCategoriesFailed,
  fetchExpertsByCategoryPending,
  fetchExpertsByCategorySucceed,
  fetchExpertsByCategoryFailed,
  fetchRandomExpertPending,
  fetchRandomExpertSucceed,
  fetchRandomExpertFailed,
  fetchExpertReviewsPending,
  fetchExpertReviewsSucceed,
  fetchExpertReviewsFailed,
} from './actions';
import { ExpertsState } from './state';

export const expertsInitialState: ExpertsState = {
  data: {},
  loadingStatus: LoadingStatuses.IDLE,
  loadingInitialExpertsStatus: LoadingStatuses.IDLE,
  totalAmount: 0,
  currentPage: 1,
  perPage: 5,
  expertReview: {
    reviews: [],
    loadingStatus: LoadingStatuses.IDLE,
  },
  currentExpert: null,
  randomExpert: {
    info: null,
    reviews: null,
    loadingStatus: LoadingStatuses.IDLE,
  },
};

export const expertsReducer = createReducer(expertsInitialState, (builder) => {
  builder
    .addCase(fetchInitialExpertsPending, (state) => {
      state.loadingInitialExpertsStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchInitialExpertsSucceed, (state, { payload: experts }) => {
      state.loadingInitialExpertsStatus = LoadingStatuses.FULFILLED;
      state.data = experts.reduce(
        (acc, expert) => ({
          ...acc,
          [expert.id]: expert,
        }),
        {},
      );
    })
    .addCase(fetchInitialExpertsFailed, (state) => {
      state.loadingInitialExpertsStatus = LoadingStatuses.FAILED;
    })
    .addCase(fetchAllExpertsPending, (state) => {
      state.loadingStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchAllExpertsSucceed, (state, { payload: experts }) => {
      state.loadingStatus = LoadingStatuses.FULFILLED;
      state.totalAmount = experts.length;
      const newExperts = experts.reduce((acc, expert) => {
        if (state.data[expert.id]) {
          state.data[expert.id] = { ...state.data[expert.id], ...expert };
          return acc;
        }

        return {
          ...acc,
          [expert.id]: expert,
        };
      }, {});
      state.data = { ...state.data, ...newExperts };
    })
    .addCase(fetchAllExpertsFailed, (state) => {
      state.loadingStatus = LoadingStatuses.FAILED;
    })

    .addCase(fetchExpertsByCategoriesPending, (state) => {
      state.loadingStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchExpertsByCategoriesSucceed, (state, { payload: experts }) => {
      state.loadingStatus = LoadingStatuses.FULFILLED;
      state.totalAmount = experts.length;
      const newExperts = experts.reduce((acc, expert) => {
        if (state.data[expert.id]) {
          state.data[expert.id] = { ...state.data[expert.id], ...expert };
          return acc;
        }

        return {
          ...acc,
          [expert.id]: expert,
        };
      }, {});
      state.data = { ...state.data, ...newExperts };
    })
    .addCase(fetchExpertsByCategoriesFailed, (state) => {
      state.loadingStatus = LoadingStatuses.FAILED;
    })
    .addCase(fetchExpertsByCategoryPending, (state) => {
      state.loadingStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchExpertsByCategorySucceed, (state, { payload: experts }) => {
      state.loadingStatus = LoadingStatuses.FULFILLED;
      state.totalAmount = experts.length;
      const newExperts = experts.reduce((acc, expert) => {
        if (state.data[expert.id]) {
          state.data[expert.id] = { ...state.data[expert.id], ...expert };
          return acc;
        }

        return {
          ...acc,
          [expert.id]: expert,
        };
      }, {});
      state.data = { ...state.data, ...newExperts };
    })
    .addCase(fetchExpertsByCategoryFailed, (state) => {
      state.loadingStatus = LoadingStatuses.FAILED;
    })
    .addCase(currentExpertUpdated, (state, { payload: expertId }) => {
      if (!expertId) {
        state.currentExpert = null;
        return;
      }

      const expert = state.data[expertId];
      if (!expert) return;
      state.currentExpert = expert;
    })
    .addCase(fetchExpertReviewsPending, (state) => {
      state.expertReview.loadingStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchExpertReviewsSucceed, (state, { payload: reviews }) => {
      state.expertReview.loadingStatus = LoadingStatuses.FULFILLED;
      state.expertReview.reviews = reviews;
    })
    .addCase(fetchExpertReviewsFailed, (state) => {
      state.expertReview.loadingStatus = LoadingStatuses.FAILED;
    })
    .addCase(fetchRandomExpertPending, (state) => {
      state.randomExpert.loadingStatus = LoadingStatuses.PENDING;
    })
    .addCase(fetchRandomExpertSucceed, (state, { payload }) => {
      const { info, reviews } = payload;
      state.randomExpert.loadingStatus = LoadingStatuses.FULFILLED;
      state.randomExpert.info = info;
      state.randomExpert.reviews = reviews;
    })
    .addCase(fetchRandomExpertFailed, (state) => {
      state.randomExpert.loadingStatus = LoadingStatuses.FAILED;
      state.randomExpert.info = null;
    });
});
